import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import toast, { Toaster } from 'react-hot-toast';

// Layout components
import DashboardLayout from '../../../examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from '../../../examples/Navbars/DashboardNavbar';

// Modals
import PaymentRequestModal from './PaymentRequestModal';
import AddPaymentModal from './AddPaymentModal';
import AddExpensesModal from './AddExpensesModal';
import AddServiceModal from './AddServiceModal';
import CancelServiceModal from './CancelServiceModal';

// Types
import { BookingData, Service, TabType } from './types/booking.types';

// Utils
import { apiCall } from '../../../utils/apiClient';

// Booking Detail Components
import BookingHeader from './components/BookingHeader';
import TabNavigation from './components/TabNavigation';
import SummaryTab from './components/SummaryTab';
import ServicesTab from './components/ServicesTab';
import PaymentsTab from './components/PaymentsTab';
import PaymentRequestsTab from './components/PaymentRequestsTab';
import ExpensesTab from './components/ExpensesTab';
import JobManagementTab from './components/JobManagementTab';
import AdjustmentsTab, { BookingAdjustment } from './components/AdjustmentsTab';
import RefundsTab from './components/RefundsTab';
import ReviewShareLinkModal from '../../reviews/ReviewShareLinkModal';

function toArray<T>(value: T[] | null | undefined): T[] {
    return Array.isArray(value) ? value : [];
}
const toNumber = (value: unknown, fallback = 0): number => {
    if (value === null || value === undefined || value === '') return fallback;
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};
const asRecord = (value: unknown): Record<string, any> =>
    value && typeof value === 'object' ? (value as Record<string, any>) : {};

const normalizeBookingData = (raw: any): BookingData | null => {
    if (!raw) return null;

    const payload = raw?.data ?? raw;
    const bookingRaw = payload?.booking ?? raw?.booking;
    const customer = asRecord(payload?.customer);
    const address = asRecord(payload?.address);
    const addressDetails = asRecord(address?.details);
    const financials = asRecord(payload?.financials);
    const financialCurrent = asRecord(financials?.current);
    const financialOriginal = asRecord(financials?.original);
    const adjustments = asRecord(payload?.adjustments);
    const adjustmentSummary = asRecord(adjustments?.summary);

    if (!bookingRaw) return null;

    const normalizeAddon = (addon: Record<string, any>): Record<string, any> => ({
        ...addon,
        addon_name: addon?.addon_name ?? addon?.name ?? '',
        addon_description: addon?.addon_description ?? addon?.description ?? '',
    });

    const normalizeService = (
        service: Record<string, any>,
        isAdditionalDefault = false
    ): Record<string, any> => ({
        ...service,
        addons: toArray(service?.addons).map(normalizeAddon),
        is_additional: service?.is_additional ?? (isAdditionalDefault ? '1' : '0'),
        discount_amount:
            service?.discount_amount ??
            service?.offer_discount ??
            service?.discount ??
            0,
    });

    const services: Record<string, any>[] = toArray<Record<string, any>>(payload?.services).map((service) =>
        normalizeService(service)
    );
    const additionalServices: Record<string, any>[] = toArray<Record<string, any>>(
        payload?.additional_services ?? payload?.additionalServices
    ).map((service) => normalizeService(service, true));

    const cancellationsFromPayload = toArray(payload?.cancellations);
    const cancellationsFromServices = [...services, ...additionalServices]
        .filter((service) => service?.cancellation)
        .map((service) => {
            const cancellation = asRecord(service.cancellation);
            return {
                id: Number(cancellation?.refund_id ?? service?.id ?? 0),
                service_id: Number(service?.id ?? 0),
                service_name: String(service?.service_name ?? '-'),
                refund_amount: toNumber(
                    cancellation?.refundable_amount ?? cancellation?.taxable_amount
                ),
                refund_reason: String(cancellation?.reason ?? '-'),
                notes: cancellation?.notes ? String(cancellation.notes) : '',
                cancelled_at: String(cancellation?.cancelled_at ?? ''),
                cancelled_by: String(cancellation?.created_by ?? 'admin'),
            };
        });

    const normalizedCancellations =
        cancellationsFromPayload.length > 0
            ? cancellationsFromPayload
            : cancellationsFromServices;

    const normalizedBooking = {
        ...bookingRaw,
        user_id: bookingRaw?.user_id ?? customer?.id ?? 0,
        user_name: bookingRaw?.user_name ?? customer?.name ?? '-',
        user_email: bookingRaw?.user_email ?? customer?.email ?? '-',
        mobile_no: bookingRaw?.mobile_no ?? customer?.mobile_no ?? '-',
        customer_address:
            bookingRaw?.customer_address ?? address?.display_address ?? '-',
        address_details:
            Object.keys(addressDetails).length > 0
                ? addressDetails
                : bookingRaw?.address_details,
        subtotal_amount:
            financialCurrent?.subtotal_amount ??
            bookingRaw?.subtotal_amount ??
            0,
        cgst: financialCurrent?.cgst_amount ?? bookingRaw?.cgst ?? 0,
        sgst: financialCurrent?.sgst_amount ?? bookingRaw?.sgst ?? 0,
        discount:
            financialCurrent?.discount_amount ??
            bookingRaw?.discount ??
            bookingRaw?.discount_amount ??
            0,
        final_amount:
            financialCurrent?.final_amount ?? bookingRaw?.final_amount ?? 0,
        paid_amount:
            financialCurrent?.paid_amount ?? bookingRaw?.paid_amount ?? 0,
        amount_due:
            financialCurrent?.amount_due ??
            financialCurrent?.due_amount ??
            bookingRaw?.amount_due ??
            bookingRaw?.due_amount ??
            0,
        created_at:
            bookingRaw?.created_at ?? bookingRaw?.booking_date ?? '',
        applied_coupon:
            bookingRaw?.applied_coupon ??
            financialCurrent?.coupon_code ??
            bookingRaw?.coupon_code ??
            null,
        total_coupon_discount:
            financialCurrent?.coupon_discount_amount ??
            financialOriginal?.coupon_discount_amount ??
            bookingRaw?.total_coupon_discount ??
            0,
        total_offer_discount:
            financialCurrent?.offer_discount_amount ??
            financialOriginal?.offer_discount_amount ??
            bookingRaw?.total_offer_discount ??
            0,
        total_discount:
            financialCurrent?.discount_amount ??
            financialOriginal?.discount_amount ??
            bookingRaw?.total_discount ??
            0,
        adjustments_total:
            financialCurrent?.adjustments_total ??
            adjustmentSummary?.net_adjustment_total ??
            bookingRaw?.adjustments_total ??
            0,
    };

    const normalizedAdjustmentItems: BookingAdjustment[] = toArray<Record<string, any>>(adjustments?.items).map(
        (item) => {
            const direction = String(item?.direction ?? '').toLowerCase();
            const lineTotal = toNumber(item?.line_total ?? item?.amount);
            const signedAmount =
                direction === 'deduction' ? -Math.abs(lineTotal) : Math.abs(lineTotal);

            return {
                id: String(item?.id ?? `ADJ-${Date.now()}`),
                type: String(item?.type ?? 'Adjustment'),
                related_to: String(item?.label ?? 'Booking'),
                amount: signedAmount,
                reason: String(item?.label ?? '-'),
                status: 'Processed',
                created_at: String(item?.created_at ?? new Date().toISOString()),
                notes: item?.created_by ? `Created by ${item.created_by}` : '',
            };
        }
    );

    return {
        ...payload,
        booking: normalizedBooking,
        services,
        additional_services: additionalServices,
        payments: toArray<Record<string, any>>(payload?.payments ?? payload?.payment).map((item) => ({
            ...item,
            payment_status: item?.payment_status ?? item?.status ?? 'pending',
            transaction_id:
                item?.transaction_id ?? item?.gateway_payment_id ?? '',
        })),
        payment_requests: toArray(payload?.payment_requests ?? payload?.paymentRequests),
        expenses: toArray(
            payload?.expenses ??
            payload?.expense ??
            payload?.expense_list ??
            bookingRaw?.expenses
        ),
        cancellations: normalizedCancellations,
        adjustment_items: normalizedAdjustmentItems,
    } as BookingData;
};

const BookingDetails: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const bookingId = location.state?.booking_id;

    const [bookingData, setBookingData] = useState<BookingData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [modalType, setModalType] = useState<string>("");
    const [activeTab, setActiveTab] = useState<TabType>("summary");
    const [selectedServices, setSelectedServices] = useState<Service[]>([]);
    const [isFullCancellation, setIsFullCancellation] = useState<boolean>(false);

    const openModal = (type: string) => setModalType(type);
    const closeModal = () => {
        setModalType("");
        setSelectedServices([]);
        setIsFullCancellation(false);
    };

    const fetchBookingDetails = async () => {
        setLoading(true);
        try {
            const res = await apiCall({
                endpoint: `booking/admin/${bookingId}`,
                method: "GET"
            });

            if (res && res.status === 200) {
                const normalized = normalizeBookingData(res.data);
                setBookingData(normalized);
            }
        } catch (error) {
            console.error('Error fetching booking details:', error);
            toast.error('Failed to fetch booking details');
        }
        setLoading(false);
    };

    useEffect(() => {
        if (bookingId) {
            fetchBookingDetails();
        }
    }, [bookingId]);

    const handleBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }

        navigate('/bookings');
    };

    const handleDelete = async (requestId: number) => {
        if (!window.confirm("Are you sure you want to delete this payment request?")) return;

        try {
            const res = await apiCall({
                endpoint: `payment/request/delete/${requestId}`,
                method: "DELETE",
            });

            if (res && res.status === 200) {
                toast.success("Payment request deleted successfully!");
                fetchBookingDetails();
            } else {
                toast.error(res?.message || "Failed to delete request");
            }
        } catch (error) {
            console.error("Error deleting payment request:", error);
            toast.error("Error deleting payment request");
        }
    };

    if (loading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <div className="flex justify-center items-center h-[75vh] w-full">
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 60 }} spin />} />
                </div>
            </DashboardLayout>
        );
    }

    if (!bookingData) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <div className="flex justify-center items-center h-[75vh] w-full">
                    <p className="text-gray-500">No booking data available</p>
                </div>
            </DashboardLayout>
        );
    }

    // =====================================================
    // Map cancellations → adjustments (DO NOT REMOVE)
    // =====================================================
    const adjustmentsFromCancellations: BookingAdjustment[] = (bookingData.cancellations || []).map(
        (item) => ({
            id: `ADJ-${item.id}`,
            type: 'Refund' as const,
            related_to: item.service_name || 'Booking',
            amount: -Number(item.refund_amount || 0),
            reason: item.refund_reason,
            status: 'Processed' as const,
            created_at: item.cancelled_at,
            notes: item.notes,
        })
    );
    const normalizedAdjustmentItems = toArray<BookingAdjustment>(
        (bookingData as any)?.adjustment_items
    );
    const adjustmentsToShow =
        normalizedAdjustmentItems.length > 0
            ? normalizedAdjustmentItems
            : adjustmentsFromCancellations;
    const refundRowsFromResponse = (() => {
        const payloadRefunds = toArray<Record<string, any>>(
            (bookingData as any)?.refunds ??
            (bookingData as any)?.refund_requests ??
            (bookingData as any)?.booking_refunds
        );
        if (payloadRefunds.length > 0) return payloadRefunds;
        return toArray<Record<string, any>>(bookingData.cancellations);
    })();

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <Toaster position="top-center" reverseOrder={false} />

                <div className="mt-6 p-6 bg-white shadow-lg rounded-lg w-full mx-auto">
                    {/* Header */}
                <BookingHeader
                    booking={bookingData.booking}
                    onBack={handleBack}
                    onCreateReviewLink={() => openModal("reviewShare")}
                />

                {/* Tabs */}
                <TabNavigation
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    servicesCount={bookingData.services?.length || 0}
                    paymentsCount={bookingData.payments?.length || 0}
                    paymentRequestsCount={bookingData.payment_requests?.length || 0}
                    expensesCount={bookingData.expenses?.length || 0}
                    refundsCount={refundRowsFromResponse.length || 0}
                />

                {/* Tab Content */}
                <div className="tab-content">
                    {activeTab === 'summary' && (
                        <SummaryTab
                            booking={bookingData.booking}
                            additionalServices={bookingData.additional_services || []}
                        />
                    )}

                    {activeTab === 'services' && (
                        <ServicesTab
                            services={bookingData.services || []}
                            additionalServices={bookingData.additional_services || []}
                            onAddService={() => openModal("addService")}
                            onCancelService={(serviceOrServices, meta) => {
                                const items = Array.isArray(serviceOrServices)
                                    ? serviceOrServices
                                    : [serviceOrServices];
                                setSelectedServices(items);
                                setIsFullCancellation(Boolean(meta?.isFullCancellation));
                                openModal("cancelService");
                            }}
                        />
                    )}

                    {activeTab === 'payments' && (
                        <PaymentsTab
                            payments={bookingData.payments || []}
                            onAddPayment={() => openModal("addPayment")}
                        />
                    )}

                    {activeTab === 'payment_requests' && (
                        <PaymentRequestsTab
                            paymentRequests={bookingData.payment_requests || []}
                            onSendRequest={() => openModal("paymentRequest")}
                            onDeleteRequest={handleDelete}
                        />
                    )}

                    {activeTab === 'expenses' && (
                        <ExpensesTab
                            expenses={bookingData.expenses || []}
                            onAddExpense={() => openModal("addExpense")}
                        />
                    )}

                    {activeTab === 'job' && (
                        <JobManagementTab
                            bookingId={bookingData.booking.id}
                            services={bookingData.services || []}
                            additionalServices={bookingData.additional_services || []}
                        />
                    )}

                    {activeTab === 'adjustments' && (
                        <AdjustmentsTab
                            bookingId={bookingData.booking.id}
                            adjustments={adjustmentsToShow}
                            hideStatus
                        />
                    )}

                    {activeTab === 'refunds' && (
                        <RefundsTab
                            bookingId={bookingData.booking.id}
                            refundsData={refundRowsFromResponse}
                            bookingCodeFallback={bookingData.booking.booking_code}
                        />
                    )}
                </div>

                {/* Modals */}
                {modalType === "addService" && (
                    <AddServiceModal
                        onClose={closeModal}
                        bookingId={bookingData.booking.id}
                        userId={bookingData.booking.user_id}
                        onSubmit={fetchBookingDetails}
                    />
                )}

                {modalType === "paymentRequest" && (
                    <PaymentRequestModal
                        onClose={closeModal}
                        bookingId={bookingData.booking.id}
                        userId={bookingData.booking.user_id}
                        onSubmit={fetchBookingDetails}
                    />
                )}

                {modalType === "addPayment" && (
                    <AddPaymentModal
                        onClose={closeModal}
                        bookingId={bookingData.booking.id}
                        userId={bookingData.booking.user_id}
                        onSubmit={fetchBookingDetails}
                    />
                )}

                {modalType === "addExpense" && (
                    <AddExpensesModal
                        onClose={closeModal}
                        bookingId={bookingData.booking.id}
                        userId={bookingData.booking.user_id}
                        onSubmit={fetchBookingDetails}
                    />
                )}

                {modalType === "cancelService" && selectedServices.length > 0 && (
                    <CancelServiceModal
                        services={selectedServices}
                        bookingId={bookingData.booking.id}
                        userId={bookingData.booking.user_id}
                        bookingSubtotal={Number(bookingData.booking.subtotal_amount || 0)}
                        bookingDiscount={Number(bookingData.booking.discount || 0)}
                        bookingFinalAmount={Number(bookingData.booking.final_amount || 0)}
                        isFullCancellation={isFullCancellation}
                        onClose={closeModal}
                        onSubmit={fetchBookingDetails}
                    />
                )}

                {modalType === "reviewShare" && (
                    <ReviewShareLinkModal
                        open
                        booking={bookingData.booking}
                        onClose={closeModal}
                    />
                )}
            </div>
        </DashboardLayout>
    );
};

export default BookingDetails;
